/* eslint-disable react/require-default-props */
import React from 'react';

import { CheckboxRoot, HiddenInput, Box, LabelText } from './styles';

export interface CheckboxProps {
  /** Estado marcado (controlado). */
  checked: boolean;
  /** Disparado ao alternar; recebe o novo estado booleano. */
  onChange: (checked: boolean) => void;
  /** Rótulo clicável à direita do box. Sem label → passar aria-label. */
  label?: React.ReactNode;
  /** Desabilita interação e ajusta visual. @default false */
  disabled?: boolean;
  /** Estado "parcial" — traço horizontal, aria-checked="mixed". @default false */
  indeterminate?: boolean;
  /** id do input (gerado automaticamente quando há label). */
  id?: string;
  /** Nome acessível quando label não é fornecido. */
  'aria-label'?: string;
  /** Classe no container externo. */
  className?: string;
}

let uidCounter = 0;

const CheckIcon = (): React.ReactElement => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M2 6.5L4.5 9L10 3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IndeterminateIcon = (): React.ReactElement => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M2.5 6H9.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

export const Checkbox = ({
  checked,
  onChange,
  label,
  disabled = false,
  indeterminate = false,
  id,
  'aria-label': ariaLabel,
  className,
}: CheckboxProps): React.ReactElement => {
  const generatedId = React.useRef(`checkbox-${(uidCounter += 1)}`).current;
  const inputId = id ?? (label ? generatedId : undefined);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!disabled) onChange(e.target.checked);
  };

  const isChecked = checked || indeterminate;

  return (
    <CheckboxRoot $disabled={disabled} className={className}>
      <HiddenInput
        type="checkbox"
        id={inputId}
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
        aria-checked={indeterminate ? 'mixed' : checked}
        aria-label={ariaLabel}
      />
      <Box $checked={isChecked} $disabled={disabled} aria-hidden="true">
        {indeterminate ? <IndeterminateIcon /> : checked && <CheckIcon />}
      </Box>
      {label && <LabelText $disabled={disabled}>{label}</LabelText>}
    </CheckboxRoot>
  );
};

export default Checkbox;
